import { supabase } from "@/integrations/supabase/client";
import type {
  DiagnosticCaptureData,
  DiagnosticCapturedEventMetadata,
  AllocationMode
} from "@/types/diagnostic";
// Core-100 R4 (CORE-AC): a Start's finish is derived by the server ('timer_started').
import { requestServerFinish } from "@/utils/serverFinishIntent";
import { triggerBoardCascade } from "@/utils/cascadeTrigger";
import { notifyDayboardInvalidated } from "@/utils/dayboardInvalidation";
import { isVehiclePresent } from "@/utils/deriveVehiclePresence";
import { reconcileActualMinutes } from "@/utils/durationProvenance";
import { logTicketEventToOcis } from "@/utils/ocisIngest";
// DTC-A: route every "current instant" read through the canonical
// clock abstraction so timer-state mutations are deterministic and
// test-freezable. `new Date(<existing-timestamp>)` calls (parsing
// stored values like session.started_at, ticket.start_time,
// openSession.paused_at) are intentionally NOT routed through the
// clock — they parse existing timestamp strings, not "now".
import { clock } from "@/utils/clock";

// Type workaround until Supabase regenerates types with new fields
type SupabaseAny = any;

/**
 * The three states the ticket-level timer projection can hold.
 *
 * Re-exported from the lifecycle module so the timer runtime and the work
 * lifecycle cannot drift into two different ideas of what a projection is.
 */
export type { TicketTimerProjection } from '@/utils/ticketWorkLifecycle';
import type { TicketTimerProjection } from '@/utils/ticketWorkLifecycle';
import type { PauseIntentFacts } from '@/lib/timer/interaction/pauseIntent';

/**
 * Narrow an RPC's timer-status field to the projection, or `null`.
 *
 * `null` means UNKNOWN — the server did not report a projection, or reported
 * something outside the three states `reconcile_ticket_timer_projection` can
 * persist. It is never widened into `'stopped'`: not knowing whether work
 * ended is a different fact from knowing it ended.
 */
function asTicketTimerProjection(value: unknown): TicketTimerProjection | null {
  return value === 'running' || value === 'paused' || value === 'stopped'
    ? value
    : null;
}

/**
 * The technician on a session the SERVER created.
 *
 * Used to give post-Resume side effects a technician identity that came from
 * the database rather than from a React prop. Returns `null` when the session
 * id is missing or the row cannot be read — callers must then broaden the side
 * effect rather than fall back to caller-supplied context, because a wrong
 * technician is a false attribution while an absent one is only a coarser
 * cascade.
 */
async function readSessionTechnicianId(
  sessionId: string | null,
): Promise<string | null> {
  if (!sessionId) return null;
  try {
    const { data, error } = await (supabase as SupabaseAny)
      .from('ticket_timer_sessions')
      .select('technician_id')
      .eq('id', sessionId)
      .maybeSingle();
    if (error) {
      console.warn('[readSessionTechnicianId] read failed', error);
      return null;
    }
    return (data?.technician_id as string) ?? null;
  } catch (e) {
    console.warn('[readSessionTechnicianId] threw', e);
    return null;
  }
}

/**
 * W4c (item 2): converted-rollover growth is written ONLY by the server.
 *
 * This used to be `syncConvertedRolloverGrowthOnBoundary`, a SECOND writer of
 * the same value (a direct CAS UPDATE of predicted_minutes +
 * accumulated_rollover_minutes from the browser at pause / resume). The
 * boundary is now server-side: a deferred trigger on `ticket_pause_sessions`
 * runs the same per-row growth body at the commit of every pause / resume
 * (migration 20270206062100), and a browser UPDATE of those fields is refused
 * (ROLLOVER_GROWTH_SERVER_OWNED). The browser only READS whether the source
 * has a live converted descendant and, if so, asks for the board cascade the
 * old writer used to trigger, so the next row re-flows after the server grew.
 */
async function cascadeAfterServerRolloverGrowth(sourceTicketId: string): Promise<void> {
  try {
    const { data: rollovers } = await (supabase as SupabaseAny)
      .from('tickets')
      .select('id, scheduled_date, shop_id, assigned_tech_id')
      .eq('rollover_of_ticket_id', sourceTicketId)
      .eq('rollover_type', 'converted')
      .in('status', ['Active', 'Paused']);
    for (const rollover of (rollovers ?? []) as Array<{ shop_id?: string; scheduled_date?: string; assigned_tech_id?: string | null }>) {
      if (rollover.shop_id && rollover.scheduled_date) {
        triggerBoardCascade(rollover.shop_id, rollover.scheduled_date, rollover.assigned_tech_id || undefined).catch(err =>
          console.warn('[cascadeAfterServerRolloverGrowth] cascade error:', err)
        );
      }
    }
  } catch (err) {
    console.warn('[cascadeAfterServerRolloverGrowth] read failed (the server growth already happened):', err);
  }
}

/**
 * 8G.2C: `closePauseSession` was REMOVED.
 *
 * It selected the newest open pause row by `ticket_id` alone — ticket-global
 * and technician-blind — and wrote `resumed_at` plus `duration_minutes` on it.
 * Its own docstring described that scope accurately and justified it on the
 * grounds that its callers were only ever ending the WHOLE ticket.
 *
 * That justification was the error. Ending a ticket is not a statement that a
 * technician resumed. MEASURED with Tech A and Tech B both paused on one
 * ticket: a manager's delete click marked TECH A's pause resumed, and only
 * afterwards did the terminal authority answer PAUSED_WORK — refusing an act
 * whose evidence it had already helped destroy.
 *
 * Both callers are gone: `cancelTicketChain` stopped calling it in 8G.2A when
 * cancellation moved to the quiescent authority, and
 * `TicketKebabMenu.handleDelete` in 8G.2C. Terminal lifecycle now REFUSES
 * while a pause is unresolved instead of resolving it on the technician's
 * behalf, so nothing legitimately needs a ticket-global pause closer.
 *
 * Resuming is `resume_my_timer`, which resolves the CALLER'S OWN pause from
 * authenticated identity. Ending paused work is `end_my_paused_work_for_review`.
 * Neither can be reached by ticket id alone, which is the point.
 */

/**
 * Auto-clock-in: PREEXISTING_UNREACHABLE_GAP.
 *
 * This block used to say auto-clock-in was handled "ONLY in the UI layer
 * (JobTimerControls.tsx)". 8F deleted that component — it had zero imports and
 * zero call sites — so the behaviour it described is not merely absent from
 * these utilities, it is absent from the product.
 *
 * Re-inventoried at 8F.1 across every live Start path
 * (`startMyTimerWithScope` -> `start_my_timer_with_scope`, and Resume via
 * `resume_my_timer`): NO auto-clock-in exists anywhere. It is not "handled
 * elsewhere"; it is unreachable, and was already unreachable before 8F deleted
 * the file, because nothing rendered that component.
 *
 * This is recorded rather than implemented. Time clock is an optional bolt-on
 * and wiring it to the timer is a product decision for whichever phase owns
 * that feature — not something to reinstate silently while closing a
 * completion-authority gap.
 */

/**
 * TIMER V2 — the canonical Start.
 *
 * ============================================================================
 * WHAT THIS REPLACED
 * ============================================================================
 * The previous `startTicketTimer` was the last runtime authority still acting
 * as though a ticket has one timer. It:
 *
 *   · took the technician and the shop from React props
 *   · read `tickets.current_session_id` and CLOSED whatever session it pointed
 *     at — so Tech B starting could stop Tech A's measured work
 *   · started with NO selected job scope, leaving the segment unattributed
 *   · performed its session INSERT and ticket UPDATE as two separate
 *     statements, so a ticket update rejected by the pre-auth trigger left an
 *     orphan RUNNING session behind
 *   · wrote `current_session_id` as if one ticket has one timer
 *   · synthesised a client-side session object
 *   · emitted a Pensieve `session.start` for EVERY job on the ticket
 *
 * All of that now happens — or no longer happens — inside
 * `start_my_timer_with_scope`, in one transaction, with identity derived from
 * `auth.uid()`.
 *
 * `selectedJobIds` is the technician's explicit assertion of what they are
 * working on. It is never inferred and never silently narrowed: a stale
 * selection comes back as `SCOPE_STALE` so the technician can look again.
 */
/**
 * NOV3 (2026-11-03): the words for a refused start / resume on a day of a multi-day repair that has been carried
 * forward (server outcome SEGMENT_ROLLED_FORWARD, or the session backstop's exception text).
 */
export function rolledForwardWords(successorDate?: unknown): string {
  if (typeof successorDate === 'string' && successorDate) {
    return `This day of the repair is closed — the work was carried forward to its next day (${successorDate}). Start or resume that day's ticket instead. Recorded time on this day is kept.`;
  }
  // 20270214260000: without a date the answer may also be an ended day with no next day yet (a manager carries it).
  return 'This day of the repair is closed — the work was carried forward to its next day, or is waiting for a manager to carry it to a new day. Continue on today\'s day of the repair. Recorded time on this day is kept.';
}

export type StartWorkResult =
  | {
      outcome: 'STARTED';
      ticketId: string;
      /** Canonical, from the ticket — not from any caller argument. */
      shopId: string;
      /** Canonical, from `auth.uid()` — not from any caller argument. */
      technicianId: string;
      sessionId: string;
      segmentId: string;
      workEpisodeId: string;
      /** Exactly what was recorded as this session's initial scope. */
      selectedJobIds: string[];
      /** The SERVER's measurement boundary. No browser clock times work. */
      startedAt: string;
      /**
       * Whether the TICKET already had work running when this Start committed,
       * decided under the ticket row lock inside the same transaction.
       */
      ticketWasRunning: boolean;
      /**
       * Whether THIS Start is the one that moved the ticket into `running`.
       *
       * A client cannot compute this. Two browsers can both read `stopped`
       * before either RPC commits, so both would claim the ticket started —
       * and `ticket_started` would be emitted twice for one transition. Exactly
       * one of two concurrent Starts on a stopped ticket carries `true`.
       */
      ticketBecameRunning: boolean;
      ticketTimerStatus: TicketTimerProjection | null;
      /** OD-33: a parked Start pulled the vehicle onto a lift with no rated capacity on record (allowed, not verified safe). */
      capacityNotVerified?: boolean;
    }
  /** This technician is already timing something, here or elsewhere. */
  | { outcome: 'ALREADY_HAS_ACTIVE_TIMER' }
  /** This technician has unreviewed work. Per technician, never shop-wide. */
  | { outcome: 'REVIEW_REQUIRED_BEFORE_NEW_TIMER'; pendingReviews: number }
  /** This technician has a paused episode HERE. Resume it; do not restart. */
  | { outcome: 'RESUME_REQUIRED' }
  /**
   * STAGE 0C — pre-repair authorization, decided by the server at every work
   * entry. ESTIMATE_REQUIRED: the shop requires authorization and the ticket
   * has no estimate to authorize. AUTHORIZATION_REQUIRED: the estimate exists
   * but the customer has not authorized it (and no manager override applies).
   * Nothing started; `fohNotified` says whether the server recorded the
   * front-of-house attention for it.
   */
  | { outcome: 'ESTIMATE_REQUIRED' | 'AUTHORIZATION_REQUIRED'; fohNotified: boolean }
  /** One or more selected jobs stopped being startable before the call landed. */
  | { outcome: 'SCOPE_STALE'; invalidJobIds: string[] }
  /**
   * The ticket does not exist, or it is in a shop this caller cannot reach.
   * DELIBERATELY the same outcome for both: distinguishing them would turn the
   * RPC into a cross-tenant existence oracle.
   */
  | { outcome: 'TICKET_NOT_AVAILABLE' }
  /** The selection itself was malformed (empty, or repeated ids). */
  | { outcome: 'INVALID_JOB_SCOPE'; reason: string }
  | { outcome: 'ESTIMATE_NOT_STARTABLE' }
  | { outcome: 'VEHICLE_NOT_PRESENT' }
  | { outcome: 'TICKET_NOT_STARTABLE' }
  | { outcome: 'NOT_TECHNICIAN' }
  /** Core-100 R4 W3 (CORE-AK): the front office holds the vehicle — no technician time is measured until it is kicked back. */
  | { outcome: 'CUSTODY_WITH_FRONT_OFFICE' }
  /**
   * STAGE 0D — the parked (pull-in) Start's own refusals. Each is returned
   * BEFORE anything moves or starts; `detail` is the server's words.
   * (Release integration: this supersedes core-prod's FLOYD-4 BAY_NOT_AVAILABLE
   * arm — same outcome, same pass-through of the server's reason.)
   *   NOT_ENTITLED_TO_TICKET  not scheduled or assigned to this technician
   *   WORK_ROLE_REFUSED       this technician's work family cannot take it
   *   ALREADY_IN_BAY          the vehicle is no longer parked (use ordinary Start)
   *   NOT_PARKED              the vehicle is not checked in to parking
   *   BAY_REQUIRED            no target bay was chosen
   *   BAY_NOT_AVAILABLE       the chosen bay refused the vehicle (occupied,
   *                           inactive, locked out, parking-only, lift capacity)
   */
  | {
      outcome: 'NOT_ENTITLED_TO_TICKET' | 'WORK_ROLE_REFUSED' | 'ALREADY_IN_BAY'
        | 'NOT_PARKED' | 'BAY_REQUIRED' | 'BAY_NOT_AVAILABLE';
      detail: string;
    }
  | { outcome: 'FAILED'; message: string };

/**
 * STAGE 0D — refusals that make a chosen pull-in bay (or the parked premise)
 * no longer valid. The caller must drop its remembered target and let the
 * technician choose again from fresh occupancy — never retry the same bay, and
 * never pick another one on their behalf.
 */
export function startRefusalInvalidatesTargetBay(outcome: StartWorkResult['outcome']): boolean {
  return outcome === 'BAY_NOT_AVAILABLE' || outcome === 'BAY_REQUIRED'
    || outcome === 'ALREADY_IN_BAY' || outcome === 'NOT_PARKED';
}

export async function startMyTimerWithScope(
  ticketId: string,
  selectedJobIds: string[],
  options?: {
    skipTimeUpdate?: boolean;
    /**
     * PTP-012 — the vehicle is PARKED and must be pulled into this bay as part
     * of starting.
     *
     * Only the ONE consequential write changes. Everything this function does
     * around that write — the pre-auth preflight and PRE_AUTH_REQUIRED mapping,
     * the OptiFinish precompute, early/late start context, outcome
     * normalisation, rollover growth sync and chain cascade, Dayboard
     * invalidation, the attempted Pensieve `session.start` event, and OCIS
     * `ticket_started` on a real transition — is the SAME Start contract and
     * must not be reimplemented beside it. A parked Start is still a Start.
     *
     * (The Pensieve write is attempted and REJECTED on every Start, parked or
     * not: `session.start` is not in `pensieve_events_event_type_check`. That
     * is a pre-existing vocabulary gap, pinned in
     * `parkedStartWrapperContract.parity.spec.ts`, not a parked-path defect.)
     */
    pullInToBayId?: string | null;
  },
): Promise<StartWorkResult> {
  console.log('[startMyTimerWithScope] Starting (self-scoped):', ticketId,
    'scope:', selectedJobIds.length, 'job(s)');

  // Canonical ticket context. `shop_id` is read here because every side effect
  // below is scoped by it and the caller has no say in the matter.
  const { data: ticketBefore } = await (supabase as SupabaseAny)
    .from('tickets')
    .select('shop_id, scheduled_date, predicted_minutes, timer_status, start_time, ' +
            'parent_ticket_id, rollover_of_ticket_id')
    .eq('id', ticketId)
    .single();

  // Early / late start, relative to the SCHEDULED start. This is scheduling
  // context, so `new Date(ticketData.start_time)` parses a stored timestamp
  // (never "now") and the comparison anchor comes from the canonical clock.
  const scheduledStart = ticketBefore?.start_time
    ? new Date(ticketBefore.start_time)
    : null;
  const nowForSchedule = clock.now();
  const isStartingEarly = !!scheduledStart && nowForSchedule < scheduledStart;
  const isStartingLate = !!scheduledStart && nowForSchedule > scheduledStart;

  const shopId: string | null = ticketBefore?.shop_id ?? null;
  // DIAGNOSTICS ONLY. This is what the browser believed before calling, and it
  // is logged when it disagrees with the server. It is NOT the authority for
  // whether the ticket-level transition occurred — see `ticketBecameRunning`.
  const statusBefore: TicketTimerProjection | null =
    asTicketTimerProjection(ticketBefore?.timer_status);

  // STAGE 0C: there is NO client pre-auth preflight. It read settings,
  // invoices and overrides through RLS that hides all three from technicians —
  // the people who press Start — so it silently did nothing for them, and for
  // managers it refused restarts the server would allow. Pre-repair
  // authorization is decided by the timer authority itself, at every work
  // entry, and comes back as a typed outcome below.

  // NOTE: `syncConvertedRolloverGrowthOnBoundary` used to run HERE, before the
  // RPC. Its disposition is recorded at its new call site below.

  // Core-100 R4 (CORE-AC): the browser no longer derives or sends a finish. Start stamps the
  // server's start; right after it, the SERVER derives the finish from that start, the canonical
  // technician, their breaks and the booked minutes ('timer_started' below) — an unknown booked
  // duration stays unknown there (NO_BOOKED_DURATION → DURATION_UNKNOWN; never an invented 60).
  // So neither a device's clock nor its arithmetic reaches the schedule.

  // ── THE ONE CONSEQUENTIAL WRITE ───────────────────────────────────────────
  // The executor is the ONLY thing a parked Start changes. Same scope, same
  // OptiFinish, same skip-time-update, same everything after.
  const pullInBayId = options?.pullInToBayId ?? null;
  const { data, error } = await (supabase.rpc as unknown as (
    fn: string, args: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message: string } | null }>)(
    pullInBayId ? 'pull_in_and_start_my_timer' : 'start_my_timer_with_scope',
    pullInBayId
      ? {
          p_ticket_id: ticketId,
          p_target_bay_id: pullInBayId,
          p_job_ids: selectedJobIds,
          p_skip_time_update: !!options?.skipTimeUpdate,
        }
      : {
          p_ticket_id: ticketId,
          p_job_ids: selectedJobIds,
          p_skip_time_update: !!options?.skipTimeUpdate,
        },
  );

  // PTP-012 — a parked Start that the timer refuses arrives as an EXCEPTION,
  // because the raise is what rolls the vehicle move back. That is deliberate
  // and is not traded away for nicer errors; instead the raise carries the WHOLE
  // refusal payload, so it is normalised below by exactly the same code that
  // normalises an ordinary Start — same outcomes, same detail fields, same
  // words on screen. A refusal that lost `pending_reviews` or `invalid_job_ids`
  // would force this layer to invent a number it does not know.
  let refusedPayload: Record<string, unknown> | null = null;

  if (error) {
    // STAGE 0C: the timer authority RETURNS a typed pre-repair refusal; the
    // legacy trigger now stands down inside it. A raised PRE_AUTH_REQUIRED can
    // only come from some other path — fail closed with the typed refusal,
    // and do not claim the front of house was told.
    if (/PRE_AUTH_REQUIRED/i.test(error.message ?? '')) {
      const estimate = /ESTIMATE_REQUIRED/i.test(error.message ?? '');
      return { outcome: estimate ? 'ESTIMATE_REQUIRED' : 'AUTHORIZATION_REQUIRED', fohNotified: false };
    }

    const marker = /PULL_IN_START_REFUSED:(\{[\s\S]*\})/.exec(error.message ?? '');
    if (marker) {
      try {
        refusedPayload = JSON.parse(marker[1]) as Record<string, unknown>;
      } catch {
        refusedPayload = null;
      }
    }

    if (!refusedPayload) {
      console.error('[startMyTimerWithScope] failed:', error);
      return { outcome: 'FAILED', message: error.message };
    }
    console.warn('[startMyTimerWithScope] parked Start refused:', refusedPayload.outcome);
  }

  const r = refusedPayload ?? ((data ?? {}) as Record<string, unknown>);
  const outcome = String(r.outcome ?? 'FAILED');

  switch (outcome) {
    case 'ALREADY_HAS_ACTIVE_TIMER':
    case 'RESUME_REQUIRED':
    case 'ESTIMATE_NOT_STARTABLE':
    case 'VEHICLE_NOT_PRESENT':
    case 'TICKET_NOT_STARTABLE':
    case 'TICKET_NOT_AVAILABLE':
    case 'NOT_TECHNICIAN':
    case 'CUSTODY_WITH_FRONT_OFFICE':
      return { outcome } as StartWorkResult;
    case 'REVIEW_REQUIRED_BEFORE_NEW_TIMER':
      return { outcome, pendingReviews: Number(r.pending_reviews ?? 0) };
    case 'SCOPE_STALE':
      return { outcome, invalidJobIds: (r.invalid_job_ids as string[]) ?? [] };
    case 'INVALID_JOB_SCOPE':
      return { outcome, reason: String(r.reason ?? 'invalid') };
    case 'ESTIMATE_REQUIRED':
    case 'AUTHORIZATION_REQUIRED':
      return { outcome, fohNotified: r.foh_notified === true };
    // STAGE 0D — every pull-in refusal is explicit; the server's reason is kept.
    case 'NOT_ENTITLED_TO_TICKET':
    case 'WORK_ROLE_REFUSED':
    case 'ALREADY_IN_BAY':
    case 'NOT_PARKED':
    case 'BAY_REQUIRED':
    case 'BAY_NOT_AVAILABLE':
      return { outcome, detail: String(r.detail ?? '') };
    // NOV3: a day of a multi-day repair that has been carried forward is closed for new work.
    case 'SEGMENT_ROLLED_FORWARD':
      return { outcome: 'FAILED', message: rolledForwardWords(r.successor_date) };
    // The pre-repair gate could not decide: refused, fail closed.
    case 'WORK_READINESS_UNKNOWN':
      return { outcome: 'FAILED', message: 'Could not confirm this ticket is authorized for work. Nothing was started.' };
    case 'STARTED':
      break;
    default:
      return { outcome: 'FAILED', message: `Unexpected start outcome: ${outcome}` };
  }

  // Unreachable by construction: the parked act only raises when the Start did
  // NOT succeed, so a refusal payload can never say STARTED. Asserted rather
  // than assumed, because everything below treats `r` as a successful start.
  if (refusedPayload) {
    return { outcome: 'FAILED', message: 'A refused parked Start reported STARTED.' };
  }

  const result: StartWorkResult = {
    outcome: 'STARTED',
    ticketId,
    shopId: String(r.shop_id ?? ''),
    technicianId: String(r.technician_id ?? ''),
    sessionId: String(r.session_id ?? ''),
    segmentId: String(r.segment_id ?? ''),
    workEpisodeId: String(r.work_episode_id ?? ''),
    selectedJobIds: (r.selected_job_ids as string[]) ?? [],
    startedAt: String(r.started_at ?? ''),
    ticketWasRunning: r.ticket_was_running === true,
    ticketBecameRunning: r.ticket_became_running === true,
    ticketTimerStatus: asTicketTimerProjection(r.ticket_timer_status),
    ...(r.capacityNotVerified === true ? { capacityNotVerified: true } : {}),
  };

  // ── SIDE EFFECTS, ALL ON CANONICAL IDENTITY ──────────────────────────────
  // Nothing below runs unless the Start above committed.
  const canonicalShop = result.shopId || shopId;

  // ROLLOVER GROWTH SYNC — CLASSIFICATION AND PLACEMENT.
  //
  // This recomputes a converted rollover descendant's accumulated minutes from
  // pause sessions that are ALREADY CLOSED. Start neither opens nor closes a
  // pause, so the value it derives is identical whether or not the Start
  // proceeds: the helper is idempotent maintenance, not a Start side effect.
  //
  // It nevertheless MOVED to after success. Its placement before the RPC was
  // inherited from the legacy Start, where it sat among other pre-work — and it
  // fires a board cascade when it writes. Running it for a Start the server
  // then refuses (ALREADY_HAS_ACTIVE_TIMER, RESUME_REQUIRED, SCOPE_STALE,
  // PRE_AUTH_REQUIRED) produced a cascade with no cause. A refused Start is not
  // a boundary; pause, resume and completion still call this at the boundaries
  // that are.
  await cascadeAfterServerRolloverGrowth(ticketId);

  // Core-100 R4 (CORE-AC): the SERVER derives this start's finish (once per start; a join of
  // already-running work, or a finish derived since, is left alone). Before the chain re-plan below,
  // which plans the unresolved future from this row's finish. Scheduling context: a failure here
  // never undoes the measured start (pushback / AutoBump re-derive on their next tick).
  if (!options?.skipTimeUpdate) {
    try {
      await requestServerFinish(ticketId, 'timer_started', { reason: 'timer_start' });
    } catch (e) {
      console.warn('[startMyTimerWithScope] server finish derivation failed (non-fatal):', e);
    }
  }

  if (!options?.skipTimeUpdate && canonicalShop && ticketBefore?.scheduled_date) {
    try {
      const { data: hasDescendants } = await supabase
        .from('tickets')
        .select('id')
        .or(`rollover_of_ticket_id.eq.${ticketId},parent_ticket_id.eq.${ticketId}`)
        // DMC-D3-FINAL: canonical active-ticket SQL filter — BOTH deleted
        // representations excluded. The status-side IN excludes
        // Completed/Cancelled/Deleted; `.is('deleted_at', null)` is
        // defence-in-depth atop the DMC-D1 primitive fix, so a ghost-row
        // descendant in either representation cannot trigger an erroneous
        // chain cascade.
        .not('status', 'in', '("Completed","Cancelled","Deleted")')
        .is('deleted_at', null)
        .limit(1);
      if (hasDescendants?.length) {
        // Starting work changes no job and no planned minutes — only when
        // this row now finishes. The chain authority re-plans the unresolved
        // future from that finish (a no-op when it is already on plan).
        const { cascadeRolloverChainAfterPushback } = await import('@/utils/rolloverChain');
        cascadeRolloverChainAfterPushback(ticketId, canonicalShop);
      }
    } catch (chainErr) {
      console.warn('[startMyTimerWithScope] Chain propagation/cascade error (non-fatal):', chainErr);
    }

    // NOT technician-scoped, for the same reason Pause and Resume are not:
    // that argument means "recompute only this technician's board", and on a
    // multi-technician ticket the starter and the assignee are different
    // people, so naming either leaves the other's board stale.
    triggerBoardCascade(canonicalShop, ticketBefore.scheduled_date);
  }

  if (canonicalShop) {
    notifyDayboardInvalidated({ reason: 'timer-update', shopId: canonicalShop, ticketId })
      .catch((e) => console.warn('[startMyTimerWithScope] dayboard broadcast failed (non-fatal):', e));
  }

  // ── PENSIEVE: ONE SESSION-LEVEL EVENT ────────────────────────────────────
  // This previously wrote one `session.start` row PER TICKET JOB, which claimed
  // the technician had started work on jobs they never selected — including
  // jobs another technician was working. `pensieve_events.job_id` is nullable
  // and the only consumer (`useTicketHistory`) keys off `event_type`, so the
  // truthful shape is one row for the SESSION carrying the selected scope.
  //
  // `selected_job_ids` is a HUMAN ASSERTION OF SCOPE, not measured per-job
  // duration. Per-job minutes come from the allocation review, and the metadata
  // says so in as many words so a later consumer cannot mistake one for the
  // other.
  if (canonicalShop) {
    try {
      const { error: pensieveErr } = await (supabase as SupabaseAny)
        .from('pensieve_events')
        .insert({
          shop_id: canonicalShop,
          ticket_id: ticketId,
          job_id: null,
          session_id: result.sessionId,
          tech_id: result.technicianId,
          event_type: 'session.start',
          timestamp: result.startedAt,
          metadata: {
            work_episode_id: result.workEpisodeId,
            segment_id: result.segmentId,
            selected_job_ids: result.selectedJobIds,
            selected_job_count: result.selectedJobIds.length,
            scope_kind: 'human_selected_work_scope',
            early_start: isStartingEarly,
            late_start: isStartingLate,
            note: 'Selected scope asserted by the technician at Start. NOT measured per-job duration — that comes from the allocation review.',
          },
        });
      // PostgREST RETURNS its errors; it does not throw them. Without this the
      // `catch` below could never run and the refusal had nowhere to surface
      // at all.
      if (pensieveErr) throw pensieveErr;
    } catch (pErr) {
      // LOUD, even though it is non-fatal. This write was refused by
      // `pensieve_events_event_type_check` on every Start for as long as the
      // event existed, and a `console.warn` inside a catch is exactly why
      // nobody noticed: the history was simply never written. A history write
      // must not fail a Start, but it must not disappear either.
      console.error(
        '[startMyTimerWithScope] pensieve session.start was REFUSED — ticket ' +
          'history for this Start does not exist. Check the event_type ' +
          'vocabulary (pensieve_events_event_type_check):',
        pErr,
      );
    }
  }

  // ── OCIS: ticket_started IS A TICKET-LEVEL CLAIM ─────────────────────────
  // Only emitted when the TICKET actually transitioned into running. If a
  // colleague was already working, the ticket never started — the technician's
  // own Start is recorded in timer evidence, and claiming the ticket started
  // again would be false.
  // The decision comes from the SERVER's transition field, not from the
  // pre-RPC read. `statusBefore` is retained above for diagnostics only: two
  // browsers can both observe `stopped` before either RPC commits, and using
  // that to gate the event would emit `ticket_started` twice for one
  // transition.
  if (canonicalShop && result.ticketBecameRunning) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await logTicketEventToOcis({
        ticketId,
        shopId: canonicalShop,
        eventType: 'ticket_started',
        content: `Timer started${isStartingEarly ? ' (early start)' : isStartingLate ? ' (late start)' : ''}`,
        authorUserId: user?.id,
        payload: {
          techId: result.technicianId,
          earlyStart: isStartingEarly,
          lateStart: isStartingLate,
          sessionId: result.sessionId,
          workEpisodeId: result.workEpisodeId,
          selectedJobIds: result.selectedJobIds,
        },
      });
    } catch (ocisErr) {
      console.error('[startMyTimerWithScope] OCIS log error:', ocisErr);
    }
  } else if (result.ticketWasRunning) {
    console.log('[startMyTimerWithScope] joined work already running — no ticket_started event emitted');
  }

  if (result.ticketBecameRunning !== (statusBefore !== 'running')) {
    // The browser's pre-read disagreed with what the server decided under the
    // ticket lock. Harmless now — the server won — but worth seeing, because
    // before 8B.1 this disagreement WAS the bug.
    console.log('[startMyTimerWithScope] pre-read disagreed with the server transition',
      { statusBefore, ticketBecameRunning: result.ticketBecameRunning });
  }

  console.log('[startMyTimerWithScope] Started', result.sessionId);
  return result;
}

/**
 * TIMER V2 — the technician's own Pause.
 *
 * Routes to `pause_my_timer`, which resolves the CALLER's own session from
 * authenticated identity. The client no longer reads
 * `tickets.current_session_id` to choose a session: on a multi-technician
 * ticket that pointer can be someone else's timer.
 *
 * Pausing is technician-scoped. If another technician is still working, the
 * ticket keeps reading `running` — so this no longer verifies a ticket-global
 * paused state, and no longer claims the whole ticket paused.
 */
export type PauseWorkResult =
  | {
      outcome: 'PAUSED';
      sessionId: string | null;
      workEpisodeId: string | null;
      /**
       * The ticket-level projection AFTER the pause — `'running' | 'paused' |
       * 'stopped'`, or `null` when the server did not report one.
       *
       * It may still be `running`: another technician's timer is unaffected by
       * yours. Widened from `string` so a consumer cannot compare it against a
       * value the projection can never hold.
       */
      ticketTimerStatus: TicketTimerProjection | null;
      /**
       * Convenience only: `ticketTimerStatus === 'running'`.
       *
       * This previously read `Boolean(r.ticket_work_running)` from the RPC
       * response — a field `pause_my_timer` does not return. It was therefore
       * ALWAYS false, and the "other work on this ticket is still running"
       * message it gates could never appear. It is now derived from the
       * projection the RPC does return.
       */
      ticketWorkRunning: boolean;
      /**
       * SERVER-DERIVED, decided under the ticket transition lock: did THIS
       * pause move ticket-level truth from running to paused?
       *
       * False when a colleague is still working. When the last two active
       * technicians pause at once, exactly ONE of them carries `true` — the
       * client cannot compute that, because both browsers read `running`
       * before either RPC commits and `paused` after both do.
       */
      ticketBecamePaused: boolean;
      /** Whether the ticket had work running before this pause. */
      ticketWasRunning: boolean;
      /**
       * STAGE 0B. SERVER-DERIVED under the same lock: after this pause, is it
       * the ONLY live BOH work on the ticket — no session still open and no
       * colleague's unresolved pause?
       *
       * This, not `ticketBecamePaused`, gates the optional whole-ticket FOH
       * handoff on Pause: `ticketBecamePaused` is also true when a colleague
       * was already paused, and a paused colleague still owns BOH work. Only a
       * literal `true` from the server counts.
       */
      pauseLeftNoOtherLiveWork: boolean;
      /**
       * STAGE D. The attention the server raised with this pause, in the same
       * transaction: 'pause_advisor_needed' | 'pause_technical_help' | null.
       * Only a Pause submitted with a structured intent can raise any.
       */
      pauseAttention: string | null;
    }
  /** THIS technician had nothing running. Not an error, and not a completion. */
  | { outcome: 'NO_ACTIVE_TIMER' }
  /** The work already went to allocation review; it is not pausable. */
  | { outcome: 'EPISODE_AWAITING_REVIEW' }
  /** Unknown ticket, or a ticket in a shop this caller cannot reach — the
   *  server deliberately does not distinguish them. */
  | { outcome: 'TICKET_NOT_AVAILABLE' }
  /** The caller is not a technician in this ticket's shop. */
  | { outcome: 'NOT_TECHNICIAN' }
  | { outcome: 'FAILED'; message: string };

/**
 * STAGE D — the RPC one Pause calls. With the technician's structured answer
 * it is `pause_my_work`, which maps the answer to its legacy reason on the
 * server, pauses through the SAME authorities below, and records the intent
 * and its attention in the same transaction. Without one (legacy callers) it
 * is the authority directly.
 */
function pauseRpc(
  ticketId: string, pauseReason: string, intent: PauseIntentFacts | undefined,
  captures: ReturnType<typeof toCapturePayload> | null,
): [string, Record<string, unknown>] {
  if (intent) {
    return ['pause_my_work', {
      p_ticket_id: ticketId, p_detail: intent.detail, p_note: intent.note,
      p_captures: captures ?? [],
    }];
  }
  return captures
    ? ['pause_my_timer_with_diagnostic_capture', { p_ticket_id: ticketId, p_pause_reason: pauseReason, p_captures: captures }]
    : ['pause_my_timer', { p_ticket_id: ticketId, p_pause_reason: pauseReason }];
}

export async function pauseTicketTimer(
  ticketId: string,
  pauseReason?: string,
  intent?: PauseIntentFacts,
): Promise<PauseWorkResult> {
  // CORE-AJ:42 — a pause without a reason must not be possible. The server
  // enforces this too; the fallback keeps existing callers working.
  const effectivePauseReason = pauseReason && pauseReason.trim().length > 0
    ? pauseReason
    : 'Paused (no reason provided)';
  console.log('[pauseTicketTimer] Pausing (self-scoped):', ticketId, 'reason:', effectivePauseReason);

  const { data: ticket } = await (supabase as SupabaseAny)
    .from('tickets')
    .select('shop_id, scheduled_date, assigned_tech_id, timer_status')
    .eq('id', ticketId)
    .single();

  const statusBefore: string | null = ticket?.timer_status ?? null;

  const { data, error } = await (supabase.rpc as unknown as (
    fn: string, args: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message: string } | null }>)(
    ...pauseRpc(ticketId, effectivePauseReason, intent, null),
  );

  if (error) {
    console.error('[pauseTicketTimer] failed:', error);
    return { outcome: 'FAILED', message: error.message };
  }

  const r = (data ?? {}) as Record<string, unknown>;
  const outcome = String(r.outcome ?? 'FAILED');

  if (outcome === 'INVALID_PAUSE_REASON') return { outcome: 'FAILED', message: 'That pause reason is not recognised.' };
  if (outcome === 'NO_ACTIVE_TIMER') return { outcome: 'NO_ACTIVE_TIMER' };
  if (outcome === 'EPISODE_AWAITING_REVIEW') return { outcome: 'EPISODE_AWAITING_REVIEW' };
  if (outcome === 'TICKET_NOT_AVAILABLE') return { outcome: 'TICKET_NOT_AVAILABLE' };
  if (outcome === 'NOT_TECHNICIAN') return { outcome: 'NOT_TECHNICIAN' };
  if (outcome !== 'PAUSED') {
    return { outcome: 'FAILED', message: `Unexpected pause outcome: ${outcome}` };
  }

  const statusAfter = asTicketTimerProjection(r.ticket_timer_status);
  const workRunning = statusAfter === 'running';
  const sessionId = (r.session_id as string) ?? null;

  // Board recomputation. This is invalidation, not evidence: the timer
  // topology changed, so the board must recalculate. It never decides timer
  // truth.
  if (ticket?.shop_id && ticket?.scheduled_date) {
    // NOT technician-scoped, and deliberately so.
    //
    // `triggerBoardCascade`'s third argument means "recompute only this
    // technician's board" (globalCascade narrows with `techsQuery.eq('id',
    // techId)`) — it is a scheduling scope, not a claim about who acted. It
    // used to receive `assigned_tech_id`, which on a multi-technician ticket
    // is frequently NOT the technician who just paused, so the pausing
    // technician's own board could be left unrecomputed. Omitting it
    // recomputes the whole shop/date: broader, but it cannot leave the actor's
    // board stale and it cannot be misread as naming the actor.
    triggerBoardCascade(ticket.shop_id, ticket.scheduled_date);
  }
  if (ticket?.shop_id) {
    notifyDayboardInvalidated({ reason: 'timer-update', shopId: ticket.shop_id, ticketId })
      .catch((e) => console.warn('[pauseTicketTimer] dayboard broadcast failed (non-fatal):', e));
  }

  // A ticket-level `ticket_paused` event is only true when the TICKET actually
  // transitioned into paused, and the SERVER decides that under the ticket
  // transition lock. The previous gate compared a client pre-read against the
  // post-read: when the last two active technicians pause at once, both
  // browsers see `running` beforehand and `paused` afterwards, so both would
  // have emitted for one transition. The technician-specific pause truth
  // already lives canonically in ticket_pause_sessions.
  if (ticket?.shop_id && r.ticket_became_paused === true) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await logTicketEventToOcis({
        ticketId,
        shopId: ticket.shop_id,
        eventType: 'ticket_paused',
        content: `Timer paused: ${effectivePauseReason}`,
        authorUserId: user?.id,
        payload: { pauseReason: effectivePauseReason, sessionId,
                   workEpisodeId: (r.work_episode_id as string) ?? null },
      });
    } catch (ocisErr) {
      console.error('[pauseTicketTimer] OCIS log error:', ocisErr);
    }
  } else if (ticket?.shop_id) {
    console.log('[pauseTicketTimer] this pause did not move the ticket into paused — no ticket_paused event emitted');
    if ((statusAfter === 'paused' && statusBefore !== 'paused')
        !== (r.ticket_became_paused === true)) {
      // The client pre/post comparison disagreed with the server's decision.
      // Harmless now; before 8B.2 this disagreement WAS the duplicate event.
      console.log('[pauseTicketTimer] client pre-read disagreed with the server transition',
        { statusBefore, statusAfter, serverBecamePaused: r.ticket_became_paused });
    }
  }

  return {
    outcome: 'PAUSED',
    sessionId,
    workEpisodeId: (r.work_episode_id as string) ?? null,
    ticketTimerStatus: statusAfter,
    ticketWorkRunning: workRunning,
    ticketBecamePaused: r.ticket_became_paused === true,
    ticketWasRunning: r.ticket_was_running === true,
    pauseLeftNoOtherLiveWork: r.pause_left_no_other_live_work === true,
    pauseAttention: typeof r.pause_attention === 'string' ? r.pause_attention : null,
  };
}

/**
 * TIMER V2 8C — record diagnostic findings AND pause, in one server call.
 *
 * ============================================================================
 * WHAT THIS REPLACED
 * ============================================================================
 * `pauseTicketTimerWithDiagnosticLock` was a findings workflow that produced
 * TIME. It computed elapsed minutes IN THE BROWSER, split them equally across
 * the captured jobs, incremented `ticket_jobs.actual_minutes`, set
 * `separate_time = true` so the timer would not allocate them again, stamped
 * the session `allocated = true` with `allocation_mode = 'diagnostic_lock'`
 * and a single `locked_job_id`, and wrote `locked_minutes` /
 * `diagnostic_minutes` into Pensieve. It found "the" session through
 * `tickets.current_session_id`, which is NULL whenever two technicians work.
 *
 * A diagnostic capture answers WHAT THE TECHNICIAN FOUND. Timer V2 answers HOW
 * MUCH TIME WAS MEASURED AND WHERE IT GOES. Those are separate facts, and the
 * old path let one invent the other.
 *
 * The browser now sends only human assertions — job, outcome, confidence,
 * complaint confirmed. The server derives the technician, shop, session,
 * episode, job name, diagnostic classification and timestamp, and the capture
 * contributes exactly zero minutes.
 */
export type DiagnosticCaptureAssertion = {
  jobId: string;
  /** What happens to the job next — the workflow disposition. */
  outcome: string;
  /** What the diagnostic work demonstrated. A different fact, never inferred. */
  competencyOutcome: string;
  confidence: number;
  complaintConfirmed: boolean;
};

export type DiagnosticCapturePauseResult =
  | (Extract<PauseWorkResult, { outcome: 'PAUSED' }> & { capturedJobCount: number })
  | { outcome: 'NO_ACTIVE_TIMER' }
  | { outcome: 'EPISODE_AWAITING_REVIEW' }
  | { outcome: 'TICKET_NOT_AVAILABLE' }
  | { outcome: 'NOT_TECHNICIAN' }
  /** One or more captured jobs are no longer capturable by THIS technician. */
  | { outcome: 'CAPTURE_SCOPE_STALE'; invalidJobIds: string[] }
  /** Somebody already recorded an observation for one of these jobs. */
  | { outcome: 'CAPTURE_ALREADY_RECORDED'; invalidJobIds: string[] }
  | { outcome: 'INVALID_CAPTURE'; reason: string }
  | { outcome: 'FAILED'; message: string };

/** The only fields the browser is allowed to assert. */
function toCapturePayload(captures: DiagnosticCaptureAssertion[]) {
  // Deliberately NOT sent: job name, capture kind, technician, shop, session,
  // episode, timestamp, minutes. A crafted client cannot label a job
  // "diagnostic" or name a performer by putting it in this array.
  return captures.map((c) => ({
    job_id: c.jobId,
    outcome: c.outcome,
    competency_outcome: c.competencyOutcome,
    confidence: c.confidence,
    complaint_confirmed: c.complaintConfirmed,
  }));
}

export async function pauseMyTimerWithDiagnosticCapture(
  ticketId: string,
  pauseReason: string,
  captures: DiagnosticCaptureAssertion[],
  intent?: PauseIntentFacts,
): Promise<DiagnosticCapturePauseResult> {
  const { data: ticket } = await (supabase as SupabaseAny)
    .from('tickets')
    .select('shop_id, scheduled_date, timer_status')
    .eq('id', ticketId)
    .single();

  const { data, error } = await (supabase.rpc as unknown as (
    fn: string, args: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message: string } | null }>)(
    ...pauseRpc(ticketId, pauseReason, intent, toCapturePayload(captures)),
  );

  if (error) {
    // The combined authority RAISEs when the canonical Pause refuses AFTER the
    // observations were written, so the whole transaction — capture included —
    // rolls back. Reporting it is the point: the technician must know their
    // findings were NOT saved rather than discover it later.
    console.error('[pauseMyTimerWithDiagnosticCapture] failed:', error);
    return { outcome: 'FAILED', message: error.message };
  }

  const r = (data ?? {}) as Record<string, unknown>;
  const outcome = String(r.outcome ?? 'FAILED');

  switch (outcome) {
    case 'NO_ACTIVE_TIMER':
    case 'EPISODE_AWAITING_REVIEW':
    case 'TICKET_NOT_AVAILABLE':
    case 'NOT_TECHNICIAN':
      return { outcome } as DiagnosticCapturePauseResult;
    case 'CAPTURE_SCOPE_STALE':
    case 'CAPTURE_ALREADY_RECORDED':
      return { outcome, invalidJobIds: (r.invalid_job_ids as string[]) ?? [] };
    case 'INVALID_CAPTURE':
      return { outcome, reason: String(r.reason ?? 'invalid') };
    case 'PAUSED':
      break;
    case 'INVALID_PAUSE_REASON':
      return { outcome: 'FAILED', message: 'That pause reason is not recognised.' };
    default:
      return { outcome: 'FAILED', message: `Unexpected capture-pause outcome: ${outcome}` };
  }

  const statusAfter = asTicketTimerProjection(r.ticket_timer_status);

  // Post-success side effects are the SAME set normal Pause performs. They are
  // not re-interpreted for the diagnostic flow: the topology change is
  // identical, only the findings differ.
  if (ticket?.shop_id && ticket?.scheduled_date) {
    triggerBoardCascade(ticket.shop_id, ticket.scheduled_date);
  }
  if (ticket?.shop_id) {
    notifyDayboardInvalidated({ reason: 'timer-update', shopId: ticket.shop_id, ticketId })
      .catch((e) => console.warn('[pauseMyTimerWithDiagnosticCapture] dayboard broadcast failed:', e));
  }

  // Server-derived, exactly as normal Pause: a diagnostic pause by one
  // technician does not pause a ticket a colleague is still working.
  if (ticket?.shop_id && r.ticket_became_paused === true) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await logTicketEventToOcis({
        ticketId,
        shopId: ticket.shop_id,
        eventType: 'ticket_paused',
        content: `Timer paused: ${pauseReason}`,
        authorUserId: user?.id,
        payload: {
          pauseReason,
          sessionId: (r.session_id as string) ?? null,
          workEpisodeId: (r.work_episode_id as string) ?? null,
          diagnosticCapturedJobCount: Number(r.captured_job_count ?? 0),
        },
      });
    } catch (ocisErr) {
      console.error('[pauseMyTimerWithDiagnosticCapture] OCIS log error:', ocisErr);
    }
  }

  return {
    outcome: 'PAUSED',
    sessionId: (r.session_id as string) ?? null,
    workEpisodeId: (r.work_episode_id as string) ?? null,
    ticketTimerStatus: statusAfter,
    ticketWorkRunning: statusAfter === 'running',
    ticketBecamePaused: r.ticket_became_paused === true,
    ticketWasRunning: r.ticket_was_running === true,
    // The combined authority returns the nested pause_my_timer result.
    pauseLeftNoOtherLiveWork: r.pause_left_no_other_live_work === true,
    pauseAttention: typeof r.pause_attention === 'string' ? r.pause_attention : null,
    capturedJobCount: Number(r.captured_job_count ?? 0),
  };
}

/**
 * The diagnostic/inspection jobs THIS technician should be asked about.
 *
 * The old client helper asked for every diagnostic job on the TICKET and
 * filtered by `separate_time` — so on a shared ticket it prompted a technician
 * for a colleague's work, and it treated "time was locked" as "the finding was
 * recorded". Candidates are now scoped to the jobs the caller's OWN work
 * episode selected, and "already recorded" means `diagnostic_completed_at`.
 */
export type DiagnosticCaptureCandidate = {
  jobId: string;
  jobName: string;
  captureKind: 'diagnostic' | 'inspection';
};

export async function readMyDiagnosticCaptureCandidates(
  ticketId: string,
): Promise<DiagnosticCaptureCandidate[]> {
  const { data, error } = await (supabase.rpc as unknown as (
    fn: string, args: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message: string } | null }>)(
    'my_diagnostic_capture_candidates', { p_ticket_id: ticketId },
  );

  if (error) {
    // An empty list is the safe answer: it asks the technician nothing rather
    // than asking them about work that may not be theirs.
    console.warn('[readMyDiagnosticCaptureCandidates] failed', error);
    return [];
  }

  const r = (data ?? {}) as Record<string, unknown>;
  if (r.outcome !== 'AVAILABLE') return [];

  return ((r.candidates as Array<Record<string, unknown>>) ?? []).map((c) => ({
    jobId: String(c.job_id),
    jobName: String(c.job_name ?? 'Job'),
    captureKind: c.capture_kind === 'inspection' ? 'inspection' : 'diagnostic',
  }));
}

/**
 * TIMER V2 — the technician's own Resume.
 *
 * Routes to `resume_my_timer`. The server derives the caller's technician
 * identity, finds THEIR unresolved canonical pause, and carries the selected
 * job scope forward from that pause's own episode lineage.
 *
 * `techId` / `shopId` are DEPRECATED COMPATIBILITY ARGUMENTS.
 *
 * They were once side-effect context — cascade targeting and broadcast scope.
 * They are not that any more either. A React prop is browser-supplied: it can
 * name a shop the ticket does not belong to and a technician who did not
 * resume anything, and if a post-success side effect trusted it, the client
 * could steer a server-truth event to the wrong tenant or attribute the resume
 * to the wrong person. Neither is timer authority and neither is now used for
 * consequential identity:
 *
 *   shop  -> `tickets.shop_id`, read canonically before the RPC
 *   tech  -> `ticket_timer_sessions.technician_id` of the session the SERVER
 *            opened; omitted (broadening the cascade) if it cannot be read
 *
 * The parameters remain in the signature so existing callers keep compiling.
 * They are ignored for anything consequential.
 */
export type ResumeWorkResult =
  | {
      outcome: 'RESUMED';
      sessionId: string | null;
      segmentId: string | null;
      workEpisodeId: string | null;
      /** Scope carried forward from the paused episode. */
      resumedJobIds: string[];
      /** Jobs dropped because they were completed/removed while paused. */
      droppedJobIds: string[];
      /**
       * The ticket-level projection AFTER the resume, or `null` when the
       * server did not report one. `null` is unknown, not `'stopped'`.
       */
      ticketTimerStatus: TicketTimerProjection | null;
      /** Whether the ticket already had work running before this resume. */
      ticketWasRunning: boolean;
      /**
       * SERVER-DERIVED, decided under the SAME ticket transition lock Start
       * uses: did THIS resume move the ticket into `running`?
       *
       * A Resume and a Start racing on one globally-paused ticket both read
       * `paused` in the browser, so both would claim the transition. Exactly
       * one carries `true`, and it decides which ticket-level event is
       * eligible — `ticket_resumed` if Resume won, `ticket_started` if Start
       * did. The loser emits no second ticket-level transition event; its
       * technician truth already lives in timer evidence.
       */
      ticketBecameRunning: boolean;
    }
  /** This technician has no unresolved pause on this ticket. */
  | { outcome: 'NO_PAUSED_WORK' }
  /** Nothing the technician was working on is still valid work. */
  | { outcome: 'SCOPE_REVIEW_REQUIRED'; droppedJobIds: string[] }
  /** A prior stopped timer's allocation must be reviewed first. */
  | { outcome: 'REVIEW_REQUIRED_BEFORE_NEW_TIMER'; pendingReviews: number }
  /** This technician is already timing something else. */
  | { outcome: 'ALREADY_HAS_ACTIVE_TIMER' }
  /** The episode went to review while this resume was in flight. */
  | { outcome: 'EPISODE_AWAITING_REVIEW' }
  /**
   * STAGE 0C — pre-repair authorization is checked at every Resume. The pause
   * is left exactly as it was; `fohNotified` says whether the server recorded
   * the front-of-house attention.
   */
  | { outcome: 'ESTIMATE_REQUIRED' | 'AUTHORIZATION_REQUIRED'; fohNotified: boolean }
  /** Unknown ticket, or a shop this caller cannot reach — not distinguished. */
  | { outcome: 'TICKET_NOT_AVAILABLE' }
  /** The caller is not a technician in this ticket's shop. */
  | { outcome: 'NOT_TECHNICIAN' }
  /** Core-100 R4 W3 (CORE-AK): the front office holds the vehicle; the pause stays as it was. */
  | { outcome: 'CUSTODY_WITH_FRONT_OFFICE' }
  /**
   * Oct 20 (D-4): the vehicle is parked and the lot cannot hold a running timer. Nothing changed; the technician is
   * guided to choose a bay and resumes through pull_in_and_resume_my_timer (one atomic move + resume).
   */
  | { outcome: 'PARKED_PULL_IN_REQUIRED'; parkingSpotId: string | null }
  /** Guided resume: the chosen bay could not take the vehicle. Nothing moved, nothing resumed. */
  | { outcome: 'BAY_NOT_AVAILABLE'; detail: string | null }
  /** Guided resume: the vehicle is not parked any more (someone moved it) — the ordinary Resume applies. */
  | { outcome: 'NOT_PARKED' }
  /** Guided resume: this technician may not hold this work (work-family eligibility). */
  | { outcome: 'WORK_ROLE_REFUSED'; detail: string | null }
  | { outcome: 'FAILED'; message: string };

export async function resumeTicketTimer(
  ticketId: string,
  /** @deprecated Ignored for identity. See the note above the type. */
  techId?: string,
  /** @deprecated Ignored for identity. See the note above the type. */
  shopId?: string,
  /** Oct 20 (D-4): resume parked paused work by bringing the vehicle into this bay in the same server act. */
  opts: { pullInToBayId?: string | null } = {},
): Promise<ResumeWorkResult> {
  console.log('[resumeTicketTimer] Resuming (self-scoped):', ticketId);
  void techId;
  void shopId;

  // `shop_id` is read here, canonically, because every post-success side
  // effect below is scoped by it. The caller's `shopId` prop is not consulted.
  const { data: ticketBefore } = await (supabase as SupabaseAny)
    .from('tickets')
    .select('timer_status, scheduled_date, shop_id')
    .eq('id', ticketId)
    .single();
  const statusBefore: string | null = ticketBefore?.timer_status ?? null;
  const canonicalShopId: string | null = ticketBefore?.shop_id ?? null;

  const { data, error } = await (supabase.rpc as unknown as (
    fn: string, args: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message: string } | null }>)(
    opts.pullInToBayId ? 'pull_in_and_resume_my_timer' : 'resume_my_timer',
    opts.pullInToBayId
      ? { p_ticket_id: ticketId, p_target_bay_id: opts.pullInToBayId }
      : { p_ticket_id: ticketId },
  );

  if (error) {
    console.error('[resumeTicketTimer] failed:', error);
    return { outcome: 'FAILED', message: error.message };
  }

  const r = (data ?? {}) as Record<string, unknown>;
  const outcome = String(r.outcome ?? 'FAILED');
  const dropped = (r.dropped_job_ids as string[]) ?? [];

  // Expected business outcomes are returned, not thrown: the UI must be able
  // to tell the technician WHY resume did not happen.
  if (outcome === 'NO_PAUSED_WORK') return { outcome: 'NO_PAUSED_WORK' };
  if (outcome === 'ALREADY_HAS_ACTIVE_TIMER') return { outcome: 'ALREADY_HAS_ACTIVE_TIMER' };
  if (outcome === 'EPISODE_AWAITING_REVIEW') return { outcome: 'EPISODE_AWAITING_REVIEW' };
  if (outcome === 'TICKET_NOT_AVAILABLE') return { outcome: 'TICKET_NOT_AVAILABLE' };
  if (outcome === 'NOT_TECHNICIAN') return { outcome: 'NOT_TECHNICIAN' };
  if (outcome === 'CUSTODY_WITH_FRONT_OFFICE') return { outcome: 'CUSTODY_WITH_FRONT_OFFICE' };
  if (outcome === 'PARKED_PULL_IN_REQUIRED') {
    return { outcome: 'PARKED_PULL_IN_REQUIRED', parkingSpotId: (r.parking_spot_id as string) ?? null };
  }
  if (outcome === 'BAY_NOT_AVAILABLE' || outcome === 'WORK_ROLE_REFUSED') {
    return { outcome, detail: (r.detail as string) ?? null };
  }
  if (outcome === 'NOT_PARKED' || outcome === 'BAY_REQUIRED') return { outcome: 'NOT_PARKED' };
  if (outcome === 'SCOPE_REVIEW_REQUIRED') {
    return { outcome: 'SCOPE_REVIEW_REQUIRED', droppedJobIds: dropped };
  }
  if (outcome === 'REVIEW_REQUIRED_BEFORE_NEW_TIMER') {
    return { outcome: 'REVIEW_REQUIRED_BEFORE_NEW_TIMER',
             pendingReviews: Number(r.pending_reviews ?? 0) };
  }
  if (outcome === 'ESTIMATE_REQUIRED' || outcome === 'AUTHORIZATION_REQUIRED') {
    return { outcome, fohNotified: r.foh_notified === true };
  }
  // NOV3: Resume on a carried-forward day (the Nov 3 field case) is refused — the work continues on today's ticket.
  if (outcome === 'SEGMENT_ROLLED_FORWARD') return { outcome: 'FAILED', message: rolledForwardWords(r.successor_date) };
  if (outcome !== 'RESUMED') {
    return { outcome: 'FAILED', message: `Unexpected resume outcome: ${outcome}` };
  }

  // ORDERING: the canonical Resume resolves the pause row and stamps its final
  // duration, so the growth calculation must run AFTER it — otherwise the
  // just-ended pause is invisible to the aggregation.
  await cascadeAfterServerRolloverGrowth(ticketId);

  const statusAfter = asTicketTimerProjection(r.ticket_timer_status);
  const resumedSessionId = (r.session_id as string) ?? null;

  // WHO the server actually resumed. `resume_my_timer` returns the session it
  // opened but not the technician on it, so the technician is read from that
  // session rather than taken from the caller's prop. `null` means "we could
  // not establish it", and nothing downstream then names anyone.
  const resumedTechnicianId = await readSessionTechnicianId(resumedSessionId);

  // Every side effect below is scoped by the TICKET's shop. If that could not
  // be read, the side effect is skipped: broadcasting to a shop the client
  // named would be worse than not broadcasting, because a wrong shop is a
  // cross-tenant event, while a missed broadcast is a stale board.
  //
  // The cascade is deliberately NOT technician-scoped. Its `techId` argument
  // means "recompute only this technician's board" (`techsQuery.eq('id',
  // techId)` in globalCascade), and on a multi-technician ticket the resuming
  // technician and the assigned one are different people — scoping to either
  // leaves the other's board stale. Omitting it recomputes the whole
  // shop/date, which is broader but correct, and it removes any chance of the
  // cascade implying who resumed.
  if (canonicalShopId && ticketBefore?.scheduled_date) {
    triggerBoardCascade(canonicalShopId, ticketBefore.scheduled_date);
  } else if (!canonicalShopId) {
    console.warn('[resumeTicketTimer] no canonical ticket shop — cascade skipped');
  }
  if (canonicalShopId) {
    notifyDayboardInvalidated({ reason: 'timer-update', shopId: canonicalShopId, ticketId })
      .catch((e) => console.warn('[resumeTicketTimer] dayboard broadcast failed (non-fatal):', e));
  }

  // Only a real ticket-level transition into running is a `ticket_resumed`
  // event, and the SERVER decides that under the ticket transition lock.
  //
  // The previous gate compared a client pre-read with a post-read — the same
  // authority defect removed from Start in 8B.1. A Resume racing a Start on one
  // globally-paused ticket sees `paused` before and `running` after on BOTH
  // sides, so both would emit a ticket-level transition event. Now exactly one
  // operation owns the transition: `ticket_resumed` if Resume won it,
  // `ticket_started` if Start did, and the joining operation emits neither.
  if (canonicalShopId && r.ticket_became_running === true) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await logTicketEventToOcis({
        ticketId,
        shopId: canonicalShopId,
        eventType: 'ticket_resumed',
        content: 'Timer resumed',
        authorUserId: user?.id,
        payload: {
          sessionId: resumedSessionId,
          workEpisodeId: (r.work_episode_id as string) ?? null,
          // The technician the SERVER resumed, or null when it could not be
          // established. Never the caller's prop: an event that names the
          // wrong person is a record, and records are read as true later.
          technicianId: resumedTechnicianId,
        },
      });
    } catch (ocisErr) {
      console.error('[resumeTicketTimer] OCIS log error:', ocisErr);
    }
  } else if (!canonicalShopId) {
    console.warn('[resumeTicketTimer] no canonical ticket shop — ticket_resumed not emitted');
  } else {
    console.log('[resumeTicketTimer] this resume joined work already running — no ticket_resumed event emitted');
    if ((statusAfter === 'running' && statusBefore !== 'running')
        !== (r.ticket_became_running === true)) {
      console.log('[resumeTicketTimer] client pre-read disagreed with the server transition',
        { statusBefore, statusAfter, serverBecameRunning: r.ticket_became_running });
    }
  }

  return {
    outcome: 'RESUMED',
    sessionId: resumedSessionId,
    segmentId: (r.segment_id as string) ?? null,
    workEpisodeId: (r.work_episode_id as string) ?? null,
    resumedJobIds: (r.resumed_job_ids as string[]) ?? [],
    droppedJobIds: dropped,
    ticketTimerStatus: statusAfter,
    ticketWasRunning: r.ticket_was_running === true,
    ticketBecameRunning: r.ticket_became_running === true,
  };
}

/**
 * Result type for ticket completion finalization
 */
interface FinalizeCompletionResult {
  success: boolean;
  allocationSuccess: boolean;
  efficiencySuccess: boolean;
  metricsSuccess: boolean;
  errors: string[];
}

/**
 * CORE-AJ / X-02 — stamp duration provenance onto a completion event.
 *
 * A `ticket_completed` event carries `actual_minutes` out of Core and into
 * Learned Intelligence. Without provenance the consumer cannot tell a measured
 * duration from one its own evidence does not support, so it would train on
 * both identically. This states which it received.
 */
function durationProvenanceMetadata(
  actualMinutes: number | null | undefined,
  sessions: readonly { duration_min?: number | null; stopped_at?: string | null }[] | null | undefined,
) {
  const r = reconcileActualMinutes(actualMinutes, sessions);
  return {
    duration_provenance: r.provenance,
    measured_minutes: r.measuredMinutes,
    duration_variance_minutes: r.varianceMinutes,
    duration_trainable: r.trainable,
  };
}

// Core-100 R4 (OD-29): the uncalled, module-private finalizeTicketCompletion() was removed. It was the only
// caller of a browser-side efficiency write; CORE-AN's server authority (core_an_apply_efficiency_learning,
// invoked at completion since OD-09) is the one writer. The algorithm survives as pure functions in
// src/utils/efficiencyLearningEngine.ts, pinned against the SQL authority by efficiencyFormulaParity.test.ts.

/**
 * TIMER V2 8C — record diagnostic findings AND stop work, in one server call.
 *
 * ============================================================================
 * WHAT THIS REPLACED
 * ============================================================================
 * `stopTicketTimerWithDiagnosticLock` carried the same defects as its Pause
 * twin — browser elapsed-time math, equal splitting, direct `actual_minutes`
 * increments, `separate_time`, session allocation flags, fabricated Pensieve
 * minutes, `current_session_id` as session authority — and then called Stop
 * separately, so a failure between the two left findings recorded against a
 * timer that never stopped, or a stopped timer with half its findings.
 *
 * This means exactly: SAVE MY FINDINGS AND STOP MY WORK FOR ALLOCATION REVIEW.
 * (This RPC alone; the technician's Stop button is `finish_my_work`, which
 * completes the ticket for the final live worker — 20270102100000.)
 * It does not complete a job, complete the ticket, prompt for pickup or
 * release the bay. The frozen allocation proposal is Timer V2's, derived from
 * the measured interval and the selected job set — the diagnostic finding does
 * not influence it.
 */
export type DiagnosticCaptureStopResult =
  | {
      outcome: 'STOPPED_AWAITING_REVIEW';
      sessionId: string;
      workEpisodeId: string;
      reviewId: string;
      measuredMinutes: number;
      attributableMinutes: number;
      ticketTimerStatus: TicketTimerProjection | null;
      ticketWorkStillRunning: boolean;
      capturedJobCount: number;
      /**
       * The nested `stop_my_timer_for_review`'s `ticket_became_idle`: TRUE only
       * when this Stop left the ticket with no open session and no unresolved
       * pause, counted under the ticket lock. Anything but a literal `true`
       * reads as `false`.
       */
      ticketBecameIdle: boolean;
    }
  | { outcome: 'NO_ACTIVE_TIMER' }
  | { outcome: 'TICKET_NOT_AVAILABLE' }
  | { outcome: 'NOT_TECHNICIAN' }
  | { outcome: 'CAPTURE_SCOPE_STALE'; invalidJobIds: string[] }
  | { outcome: 'CAPTURE_ALREADY_RECORDED'; invalidJobIds: string[] }
  | { outcome: 'INVALID_CAPTURE'; reason: string }
  | { outcome: 'FAILED'; message: string };

export async function stopMyTimerWithDiagnosticCapture(
  ticketId: string,
  captures: DiagnosticCaptureAssertion[],
): Promise<DiagnosticCaptureStopResult> {
  const { data: ticket } = await (supabase as SupabaseAny)
    .from('tickets')
    .select('shop_id, scheduled_date')
    .eq('id', ticketId)
    .single();

  const { data, error } = await (supabase.rpc as unknown as (
    fn: string, args: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message: string } | null }>)(
    'stop_my_timer_with_diagnostic_capture', {
      p_ticket_id: ticketId,
      p_captures: toCapturePayload(captures),
    },
  );

  if (error) {
    console.error('[stopMyTimerWithDiagnosticCapture] failed:', error);
    return { outcome: 'FAILED', message: error.message };
  }

  const r = (data ?? {}) as Record<string, unknown>;
  const outcome = String(r.outcome ?? 'FAILED');

  switch (outcome) {
    case 'NO_ACTIVE_TIMER':
    case 'TICKET_NOT_AVAILABLE':
    case 'NOT_TECHNICIAN':
      return { outcome } as DiagnosticCaptureStopResult;
    case 'CAPTURE_SCOPE_STALE':
    case 'CAPTURE_ALREADY_RECORDED':
      return { outcome, invalidJobIds: (r.invalid_job_ids as string[]) ?? [] };
    case 'INVALID_CAPTURE':
      return { outcome, reason: String(r.reason ?? 'invalid') };
    case 'STOPPED_AWAITING_REVIEW':
      break;
    default:
      return { outcome: 'FAILED', message: `Unexpected capture-stop outcome: ${outcome}` };
  }

  const statusAfter = asTicketTimerProjection(r.ticket_timer_status);

  if (ticket?.shop_id && ticket?.scheduled_date) {
    triggerBoardCascade(ticket.shop_id, ticket.scheduled_date);
  }
  if (ticket?.shop_id) {
    notifyDayboardInvalidated({ reason: 'timer-update', shopId: ticket.shop_id, ticketId })
      .catch((e) => console.warn('[stopMyTimerWithDiagnosticCapture] dayboard broadcast failed:', e));
  }
  // NO `ticket_completed`, and no new ticket-level event. Stopping work is not
  // completing the visit, and the stopped episode plus the diagnostic capture
  // are already durable facts.

  return {
    outcome: 'STOPPED_AWAITING_REVIEW',
    sessionId: String(r.session_id ?? ''),
    workEpisodeId: String(r.work_episode_id ?? ''),
    reviewId: String(r.review_id ?? ''),
    measuredMinutes: Number(r.measured_minutes ?? 0),
    attributableMinutes: Number(r.attributable_minutes ?? 0),
    ticketTimerStatus: statusAfter,
    ticketWorkStillRunning: statusAfter === 'running',
    capturedJobCount: Number(r.captured_job_count ?? 0),
    ticketBecameIdle: r.ticket_became_idle === true,
  };
}

/**
 * 8F: `StopTicketTimerResult` removed with `stopTicketTimer`. Its only
 * producer was the deprecated completion funnel; no caller consumed it.
 */


/**
 * 8F REMOVED: `stopTicketTimer`.
 *
 * It was the legacy completion funnel — it marked EVERY job on the ticket
 * completed, wrote actual_minutes and actual_finish_time directly, derived a
 * performer in TypeScript, and carried terminal side effects (pickup prompt,
 * Pensieve ticket_completed, parking/bay handling). Measured at 8F: ZERO
 * production call sites — every reference left in the tree was a comment.
 *
 * Its behaviour was NOT migrated. Job completion now belongs to the single
 * server authority `complete_reviewed_ticket_jobs`, which requires reviewed
 * evidence and refuses to complete work another technician is still doing;
 * ticket-level performer attribution is derived by `complete_ticket_if_eligible`
 * from measured participation, not in the browser.
 *
 * The terminal side effects it used to carry are TERMINAL_LIFECYCLE_DEFERRED
 * and belong to the ticket-closeout phase.
 */

export async function allocateTicketTime(
  ticketId: string,
  sessionIds?: string[]
) {
  console.log('Triggering time allocation for ticket:', ticketId, 'sessions:', sessionIds);

  try {
    const { data, error } = await supabase.functions.invoke('allocate-ticket-time', {
      body: { 
        ticketId,
        sessionIds // Pass session IDs if provided
      }
    });

    if (error) {
      console.error('Error allocating ticket time:', error);
      throw error;
    }

    console.log('Time allocation result:', data);
    return data;
  } catch (error) {
    console.error('Failed to allocate ticket time:', error);
    throw error;
  }
}

export async function getTicketTotalActualMinutes(ticketId: string): Promise<number> {
  const { data: sessions, error } = await (supabase as SupabaseAny)
    .from('ticket_timer_sessions')
    .select('duration_min')
    .eq('ticket_id', ticketId)
    .not('stopped_at', 'is', null);

  if (error) {
    console.error('Error fetching timer sessions:', error);
    return 0;
  }

  return sessions?.reduce((sum: number, s: any) => sum + (s.duration_min || 0), 0) || 0;
}

/**
 * 8G.3A.1 — `cancelTicketChain` is RETIRED.
 *
 * It cancelled a rollover descendant and then emitted a Pensieve
 * `ticket.cancelled` record, an OCIS timeline entry and a board cascade.
 * Its only caller was `RolloverCompletionModal.handleAllSegments`, and that
 * surface could never open: `rollover-completion-prompt` had a listener and
 * no dispatcher anywhere in the product — `promptRolloverCompletionIfNeeded`,
 * which the audits still name, no longer exists.
 *
 * It owned no canonical fact by the time it went. Terminal state was already
 * `cancel_ticket_if_quiescent`; the TICKET_SOFT_DELETED audit moved to the
 * server authority in 8G.3A; and its Pensieve, OCIS and cascade writes are
 * each duplicated by the live kebab family-delete path. Removing it removed
 * an unreachable second way to cancel a ticket, not a behaviour.
 *
 * The rollover-completion PRODUCT decision is deferred, not answered here. A
 * future phase that designs a reachable rollover-completion UX builds it on
 * the canonical authority; it does not restore this helper.
 */
